import React, { useState, useEffect, useRef } from "react";
import api from "../../lib/api";
import { Plus, Edit2, Trash2, X, Save, MessageSquare, ChevronDown, ChevronRight } from "lucide-react";
import { useConfirm } from "../../contexts/ConfirmContext";

const STATUS_OPTIONS = [
  { value: "open", label: "Offen", cls: "status-open" },
  { value: "in_progress", label: "In Bearbeitung", cls: "status-in-progress" },
  { value: "done", label: "Erledigt", cls: "status-done" },
];
const PRIORITY_OPTIONS = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
];

// ── Draggable Card ─────────────────────────────────────────────────────
function TicketCard({ ticket, customerId, users, onDelete, onUpdate }) {
  const [dragging, setDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: ticket.title, description: ticket.description || "", priority: ticket.priority, assigned_to: ticket.assigned_to || "" });
  const [commentText, setCommentText] = useState("");

  const save = async (e) => {
    e.preventDefault();
    const r = await api.put(`/customers/${customerId}/tickets/${ticket._id}`, form);
    onUpdate(r.data);
    setEditing(false);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    await api.post(`/customers/${customerId}/tickets/${ticket._id}/comments`, { text: commentText });
    const r = await api.get(`/customers/${customerId}/tickets`);
    const updated = r.data.find(t => t._id === ticket._id);
    if (updated) onUpdate(updated);
    setCommentText("");
  };

  return (
    <div
      data-testid={`ticket-card-${ticket._id}`}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData("ticketId", ticket._id);
        e.dataTransfer.setData("customerId", customerId);
        e.dataTransfer.effectAllowed = "move";
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      className={`kanban-card bg-card border border-border mb-2 cursor-grab active:cursor-grabbing transition-opacity ${dragging ? "opacity-40" : ""}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm leading-snug">{ticket.title}</p>
            {ticket.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ticket.description}</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => { setEditing(!editing); setExpanded(true); }} className="text-muted-foreground hover:text-primary transition-colors p-0.5"><Edit2 size={12} /></button>
            <button onClick={() => onDelete(ticket._id)} className="text-muted-foreground hover:text-destructive transition-colors p-0.5"><Trash2 size={12} /></button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-1.5 py-0.5 priority-${ticket.priority}`}>
              {PRIORITY_OPTIONS.find(p => p.value === ticket.priority)?.label}
            </span>
            {ticket.assigned_initials && (
              <span className="initials-avatar w-6 h-6 text-xs" title={ticket.assigned_name}>{ticket.assigned_initials}</span>
            )}
          </div>
          <button
            data-testid={`toggle-comments-${ticket._id}`}
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title="Kommentare"
          >
            <MessageSquare size={11} />{ticket.comments?.length || 0}
            {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </button>
        </div>
      </div>

      {editing && (
        <div className="border-t border-border p-3 bg-muted/20">
          <form onSubmit={save} className="space-y-2">
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
              className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-none" />
            <div className="flex gap-2">
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="flex-1 px-2 py-1.5 text-xs bg-background border border-input focus:border-primary focus:outline-none">
                {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                className="flex-1 px-2 py-1.5 text-xs bg-background border border-input focus:border-primary focus:outline-none">
                <option value="">Nicht zugewiesen</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.initials})</option>)}
              </select>
            </div>
            <div className="flex gap-1.5">
              <button type="submit" className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1"><Save size={11} /> Speichern</button>
              <button type="button" onClick={() => setEditing(false)} className="px-2 py-1 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={11} /> Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      {expanded && (
        <div className="border-t border-border p-3 bg-muted/20 space-y-2">
          {ticket.comments?.length > 0 ? (
            ticket.comments.map((c, i) => (
              <div key={i} className="flex gap-2 text-xs">
                <span className="initials-avatar w-5 h-5 text-xs flex-shrink-0">{c.user_initials}</span>
                <div>
                  <span className="font-semibold">{c.user_name}</span>
                  <span className="text-muted-foreground ml-1">{new Date(c.created_at).toLocaleString("de-CH")}</span>
                  <p className="mt-0.5">{c.text}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground italic">Noch keine Kommentare</p>
          )}
          <form onSubmit={addComment} className="flex gap-2 mt-2">
            <input
              data-testid={`comment-input-${ticket._id}`}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Kommentar hinzufügen..."
              className="flex-1 px-2 py-1 text-xs bg-background border border-input focus:border-primary focus:outline-none"
            />
            <button type="submit" className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold">Senden</button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Drop Column ────────────────────────────────────────────────────────
function KanbanColumn({ status, label, cls, tickets, users, customerId, onDrop, onDelete, onUpdate }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`kanban-col p-3 transition-all duration-150 ${dragOver ? "ring-2 ring-primary/40 bg-primary/5" : ""}`}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        const ticketId = e.dataTransfer.getData("ticketId");
        if (ticketId) onDrop(ticketId, status);
      }}
      data-testid={`kanban-col-${status}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-semibold px-2 py-0.5 ${cls}`}>{label}</span>
        <span className="text-xs font-mono text-muted-foreground">{tickets.length}</span>
      </div>
      {tickets.map(t => (
        <TicketCard key={t._id} ticket={t} customerId={customerId} users={users} onDelete={onDelete} onUpdate={onUpdate} />
      ))}
      {tickets.length === 0 && (
        <div className={`border-2 border-dashed border-border rounded-sm p-4 text-center text-xs text-muted-foreground ${dragOver ? "border-primary/50" : ""}`}>
          Hierher ziehen
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────
export default function TicketsTab({ customerId, openFormTrigger }) {
  const confirm = useConfirm();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "open", priority: "medium", assigned_to: "" });

  useEffect(() => {
    api.get(`/customers/${customerId}/tickets`).then(r => setTickets(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, [customerId]);

  // External trigger (from hero "Neues Ticket" button)
  useEffect(() => {
    if (openFormTrigger) setShowForm(true);
  }, [openFormTrigger]);

  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.assigned_to) delete payload.assigned_to;
    const r = await api.post(`/customers/${customerId}/tickets`, payload);
    setTickets([r.data, ...tickets]);
    setForm({ title: "", description: "", status: "open", priority: "medium", assigned_to: "" });
    setShowForm(false);
  };

  const remove = async (id) => {
    if (!(await confirm({
      title: "Ticket löschen",
      message: "Dieses Ticket inkl. aller Kommentare wird gelöscht.",
      confirmLabel: "Löschen",
    }))) return;
    await api.delete(`/customers/${customerId}/tickets/${id}`);
    setTickets(tickets.filter(t => t._id !== id));
  };

  const update = (updated) => setTickets(prev => prev.map(t => t._id === updated._id ? updated : t));

  const handleDrop = async (ticketId, newStatus) => {
    const ticket = tickets.find(t => t._id === ticketId);
    if (!ticket || ticket.status === newStatus) return;
    // Optimistic update
    setTickets(prev => prev.map(t => t._id === ticketId ? { ...t, status: newStatus } : t));
    try {
      const r = await api.put(`/customers/${customerId}/tickets/${ticketId}`, { status: newStatus });
      setTickets(prev => prev.map(t => t._id === ticketId ? r.data : t));
    } catch {
      setTickets(prev => prev.map(t => t._id === ticketId ? ticket : t)); // rollback
    }
  };

  const byStatus = (s) => tickets.filter(t => t.status === s);

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Ticket-Board</h2>
        <button data-testid="add-ticket-button" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">
          <Plus size={13} /> Neues Ticket
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-card border border-border p-4 mb-4 animate-in" data-testid="create-ticket-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Titel *</label>
              <input data-testid="ticket-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Priorität</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Beschreibung</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Zuweisen an</label>
              <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                <option value="">Nicht zugewiesen</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.initials})</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="save-ticket-button" type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold">Erstellen</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold">Abbrechen</button>
          </div>
        </form>
      )}

      <p className="text-xs text-muted-foreground mb-3">Tickets per Drag &amp; Drop zwischen Spalten verschieben</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
        {STATUS_OPTIONS.map(s => (
          <KanbanColumn key={s.value} status={s.value} label={s.label} cls={s.cls}
            tickets={byStatus(s.value)} users={users} customerId={customerId}
            onDrop={handleDrop} onDelete={remove} onUpdate={update} />
        ))}
      </div>
    </div>
  );
}
